import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import AreaFeature from './AreaFeature';
import { useImagesLoaded } from '../../hooks/useImagesLoaded';
import { COLOR_BLACK, COLOR_WHITE, COLOR_STEAK_SAUCE } from '../colors';
import queryString from 'query-string';

const ROOT_WIDTH = 900;
const ROOT_HEIGHT = 600;
const SPARKLES_URL = './images/ListingShareable/sparkles.svg';
const PIXEL_DENSITY_FACTOR = 2;
const KEYFRAME_COUNT = 9;
const KEYFRAME_INTERVAL = 1; // number of frames per keyframe
const HERO_COUNT = 3;
const HERO_KEYFRAME_INTERVAL = (KEYFRAME_COUNT * KEYFRAME_INTERVAL) / HERO_COUNT;
const LAST_KEYFRAME = ((KEYFRAME_COUNT - 1) * KEYFRAME_INTERVAL) + 1;
const getScaleFactor = (props)=> props && !isNaN(props.scaleFactor) ? props.scaleFactor : PIXEL_DENSITY_FACTOR;

const useStyles = makeStyles((theme)=> ({
    root: {
        width: (props)=> ROOT_WIDTH * getScaleFactor(props),
        height: (props)=> ROOT_HEIGHT * getScaleFactor(props),
        position: 'relative',
        fontFamily: ['"LL Circular Pro"', 'Arial', 'Helvetica', 'sans-serif'],
        overflow: 'hidden',
        backgroundColor: COLOR_WHITE,
        textRendering: 'optimizeLegibility',
        borderRadius: (props)=> props && props.borderRadius ? props.borderRadius : 0,
    },
    viewport: {
        display: 'block',
        position: 'relative',
        width: '100%',
        height: '100%',
        zoom: (props)=> getScaleFactor(props),
    },
    leftWrap: {
        position: 'relative',
        width: '50%',
        height: '100%',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        color: COLOR_WHITE,
    },
    shadowGradient: {
        position: 'absolute',
        width: '100%',
        height: 120,
        bottom: 0,
        left: 0,
        background: 'linear-gradient(0deg, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0) 100%)',
    },
    listingStatusWrap: {
        position: 'absolute',
        width: 278,
        height: 104,
        bottom: 80,
        left: 0,
    },
    listingStatusContainer: {
        position: 'relative',
        height: '100%',
        width: 224,
        backgroundColor: COLOR_BLACK,

        '&::before': {
            position: 'absolute',
            right: -52,
            top: 0,
            content: "''",
            height: 104,
            width: 52,
            backgroundColor: COLOR_BLACK,
            borderTopRightRadius: 52,
            borderBottomRightRadius: 52,
        },
    },
    listingStatusLabel: {
        position: 'absolute',
        fontSize: 36,
        left: 24,
        top: 38,
        fontWeight: 800,
        lineHeight: '28px',
        whiteSpace: 'nowrap',
    },
    sparklesLeft: {
        position: 'absolute',
        left: 24,
        bottom: -22,
    },
    sparklesRight: {
        position: 'absolute',
        right: -64,
        top: -16,

    },
    cityName: {
        position: 'absolute',
        fontSize: 22,
        fontWeight: 500,
        lineHeight: '32px',
        left: 24,
        bottom: 24,
    },
    areaInfographicWrap: {
        position: 'absolute',
        top: 51,
        right: 0,
        height: 472,
        width: 513,
        color: COLOR_BLACK,
    },
    relativeDiv: {
        position: 'relative',
        width: '100%',
        height: '100%',
    },
    walkPathTop: {
        position: 'absolute',
        top: 50,
        right: 7,
        height: 180,
        width: 450,
        border: `4px solid ${COLOR_BLACK}`,
        borderRadius: '0 50px 50px 0',
        borderLeftColor: 'transparent',
    },
    walkPathBottom: {
        position: 'absolute',
        top: 234,
        left: 0,
        height: 180,
        width: 450,
        border: `4px solid ${COLOR_BLACK}`,
        borderRadius: '50px 0 0 50px',
        borderRightColor: 'transparent',
    },
    exploreNearbyPill: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: 204,
        height: 104,
        backgroundRepeat: 'no-repeat',
        backgroundImage: 'url("./images/ListingShareable/map-pin-pill.svg")',
    },
    exploreNearby: {
        marginTop: 24,
        marginLeft: 80,
        width: 94,
    },
    getInTouchPill: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: 277,
        height: 104,
        backgroundRepeat: 'no-repeat',
        backgroundImage: 'url("./images/ListingShareable/get-in-touch-pill.svg")',
    },
    getInTouch: {
        marginTop: 24,
        marginLeft: 41,
        width: 224,
    },
    ctaText: {
        fontSize: '26px',
        lineHeight: '28px',
        fontWeight: 800,
    },
    disclaimerWrap: {
        position: 'absolute',
        width: 230,
        bottom: 16,
        right: 24,
        color: COLOR_STEAK_SAUCE,
        textAlign: 'right',
        fontSize: 10,
        lineHeight: '12px',
    },
    disclaimer: {
        marginTop: 5,
    },
}));


const useAnimationStyles = makeStyles((theme)=> ({
    exploreNearbyPill: {
        backgroundImage: 'url("./images/ListingShareable/map-pin-pill-dark.svg")',
        color: COLOR_WHITE,
    },
    getInTouchPill: {
        backgroundImage: 'url("./images/ListingShareable/get-in-touch-pill-dark.svg")',
        color: COLOR_WHITE,
    }
}));

export default function ListingShareable(props) {
    const classes = useStyles(props);
    const animationClasses = useAnimationStyles();
    const location = useLocation();
    let parsedData = props.data || null;
    let currentFrame = props.frame || 1;
    const onImagesLoaded = props.onImagesLoaded;
    if(!parsedData) {
        try {
            const { data, frame } = queryString.parse(location.search);
            parsedData = JSON.parse(data);
            currentFrame = parseInt(frame, 10);
        } catch(e) {
            console.error(e);
        }
    }
    const { city, hero, photos, area = [], listing_status_label } = parsedData || {};
    const photoIndex = Math.floor(currentFrame / HERO_KEYFRAME_INTERVAL);
    const heroImage = (photos && photos.length)
        ? photos[photoIndex < HERO_COUNT ? photoIndex : 0] || photos[photos.length - 1]
        : hero;
    const loadedBgs = useImagesLoaded([heroImage]);

    useEffect(() => {
        if(loadedBgs && !window.isLoaded) {
            if(typeof onImagesLoaded === 'function') {
                onImagesLoaded();
            } else {
                window.isLoaded = true;
            }
        }
    }, [loadedBgs, onImagesLoaded]);

    if(!parsedData) return null;

    while(area && area.length < 6) {
        area.push({ category: 'n/a' });
    }

    return (
        <>
            <link rel="stylesheet" href="./css/ll-circular-pro.css" />
            <div className={classes.root} id="templateRoot">
                <div className={classes.viewport}>

                    {/* Left Block */}

                    <div className={classes.leftWrap}
                        style={{
                            backgroundImage: `url("${heroImage}")`
                        }}
                    >
                        <div className={classes.shadowGradient}></div>
                        <div className={classes.listingStatusWrap}>
                            <div className={classes.listingStatusContainer}>
                                <div className={classes.listingStatusLabel}>{(listing_status_label || 'JUST LISTED').toUpperCase()}</div>
                                <img className={classes.sparklesLeft} src={SPARKLES_URL} alt="sparkles"/>
                                <img className={classes.sparklesRight} src={SPARKLES_URL} alt="sparkles"/>
                            </div>
                        </div>
                        <div className={classes.cityName}>in {city}</div>
                    </div>


                    <div className={classes.areaInfographicWrap}>
                        <div className={classes.relativeDiv}>

                            {/* Walking Path Stroke */}

                            <div className={classes.walkPathTop}></div>
                            <div className={classes.walkPathBottom}></div>

                            {/* Explore Nearby */}

                            <div className={`${classes.exploreNearbyPill} ${currentFrame > KEYFRAME_INTERVAL ? animationClasses.exploreNearbyPill : ''}`}>
                                <div className={classes.relativeDiv}>
                                    <div className={`${classes.exploreNearby} ${classes.ctaText}`}>
                                        Explore Nearby
                                    </div>
                                </div>
                            </div>

                            {/* Area Features */}
                            {area && (
                                area
                                    .slice(0, 6)
                                    .map((feature, i)=>  (
                                    <AreaFeature
                                        key={`feature_block_${feature.category}_${i}`}
                                        feature={feature}
                                        index={i}
                                        keyframeInterval={KEYFRAME_INTERVAL}
                                        currentFrame={currentFrame}
                                    />
                                ))
                            )}

                            {/* Get in touch */}

                            <div className={`${classes.getInTouchPill} ${currentFrame >= LAST_KEYFRAME ? animationClasses.getInTouchPill : ''}`}>
                                <div className={classes.relativeDiv}>
                                    <div className={`${classes.getInTouch} ${classes.ctaText}`}>
                                        Get in touch with
                                        me for more info!
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>


                    {/* Disclaimer */}
                    <div className={classes.disclaimerWrap}>
                        <img src="./images/lr-logo-disclaimer-grey.svg" height={11} alt="LR Logo" />
                        <div className={classes.disclaimer}>
                            The information in this report is from third-party sources
                            and its accuracy cannot be guaranteed
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
